import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Mail, Lock, User, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage() {
  const [mode, setMode] = useState('login'); // login, register, verify, forgot, reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'register') {
        if (!name.trim()) { toast.error('Name is required'); setLoading(false); return; }
        if (!ageConfirmed) { toast.error('You must confirm you are 16 or older'); setLoading(false); return; }
        const res = await register(email, password, name, true);
        toast.success('Account created! Check your email for a verification code.');
        setMode('verify');
      } else if (mode === 'login') {
        try {
          const res = await login(email, password);
          if (res?.requires_verification) {
            toast.info('Please verify your email first');
            setMode('verify');
          } else {
            toast.success('Welcome back!');
            navigate(res.onboarding_completed ? '/dashboard' : '/onboarding');
          }
        } catch (err) {
          // Check if the response indicates verification needed
          if (err.response?.data?.requires_verification) {
            setEmail(err.response.data.email || email);
            toast.info('Please verify your email first');
            setMode('verify');
          } else {
            throw err;
          }
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.verifyEmail({ email, code: verificationCode });
      toast.success('Email verified! You can now sign in.');
      setMode('login');
      setVerificationCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await authAPI.resendVerification({ email });
      toast.success('New verification code sent!');
    } catch (err) {
      toast.error('Failed to resend code');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword({ email });
      toast.success('If this email is registered, a reset code has been sent.');
      setMode('reset');
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword({ email, code: resetCode, new_password: newPassword });
      toast.success('Password reset! You can now sign in.');
      setMode('login');
      setResetCode('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'register': return 'Create account';
      case 'verify': return 'Verify your email';
      case 'forgot': return 'Forgot password';
      case 'reset': return 'Reset password';
      default: return 'Welcome back';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'register': return 'Start your personal brand journey';
      case 'verify': return `We sent a 6-digit code to ${email}`;
      case 'forgot': return 'Enter your email to receive a reset code';
      case 'reset': return `Enter the code sent to ${email}`;
      default: return 'Sign in to your Brand OS';
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="auth-page">
      <div className="px-6 pt-6">
        <button
          data-testid="auth-back-btn"
          onClick={() => {
            if (mode === 'verify' || mode === 'forgot' || mode === 'reset') {
              setMode('login');
            } else {
              navigate('/');
            }
          }}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600"
          style={{ transition: 'color 0.2s' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {mode === 'verify' || mode === 'forgot' || mode === 'reset' ? 'Back to login' : 'Back'}
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10 animate-fade-in-up">
            <div className="w-11 h-11 bg-purple-200 rounded-xl flex items-center justify-center shadow-inner">
              <span className="text-white font-bold text-xl font-['Manrope']">B</span>
            </div>
            <span className="text-xl font-semibold tracking-tight font-['Manrope']">Brand OS</span>
          </div>

          <h1 className="text-3xl font-bold tracking-tight mb-2 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
            {getTitle()}
          </h1>
          <p className="text-gray-400 mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            {getSubtitle()}
          </p>

          {/* Login / Register Form */}
          {(mode === 'login' || mode === 'register') && (
            <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-gray-600">Name</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    <Input data-testid="auth-name-input" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400" style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }} />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="auth-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400" style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-600">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="auth-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400" style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }} />
                </div>
              </div>

              {mode === 'register' && (
                <div className="flex items-start gap-3 py-2">
                  <Checkbox
                    id="age-confirm"
                    data-testid="auth-age-checkbox"
                    checked={ageConfirmed}
                    onCheckedChange={(checked) => setAgeConfirmed(checked === true)}
                    className="mt-0.5 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                  />
                  <Label htmlFor="age-confirm" className="text-sm text-gray-500 leading-relaxed cursor-pointer">
                    I confirm that I am <strong>16 years of age or older</strong> and agree to the Terms of Service.
                  </Label>
                </div>
              )}

              {mode === 'login' && (
                <button
                  type="button"
                  data-testid="auth-forgot-password-btn"
                  onClick={() => setMode('forgot')}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  style={{ transition: 'color 0.2s' }}
                >
                  Forgot your password?
                </button>
              )}

              <Button data-testid="auth-submit-btn" type="submit" disabled={loading || (mode === 'register' && !ageConfirmed)} className="w-full rounded-full h-14 bg-[#09090b] text-white hover:opacity-90 text-base font-semibold shadow-lg shadow-purple-500/10 active:scale-95 disabled:opacity-50" style={{ transition: 'opacity 0.2s, transform 0.15s' }}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
          )}

          {/* Email Verification Form */}
          {mode === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-medium text-gray-600">Verification Code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input
                    data-testid="auth-verification-code-input"
                    id="code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base text-center tracking-[0.5em] font-mono focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400 placeholder:tracking-normal"
                    style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
                  />
                </div>
              </div>

              <Button data-testid="auth-verify-btn" type="submit" disabled={loading || verificationCode.length !== 6} className="w-full rounded-full h-14 bg-[#09090b] text-white hover:opacity-90 text-base font-semibold active:scale-95 disabled:opacity-50" style={{ transition: 'opacity 0.2s, transform 0.15s' }}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Email'}
              </Button>

              <p className="text-center text-sm text-gray-400">
                Didn't receive the code?{' '}
                <button type="button" data-testid="auth-resend-code-btn" onClick={handleResendCode} className="text-purple-600 font-medium hover:text-purple-700" style={{ transition: 'color 0.2s' }}>
                  Resend
                </button>
              </p>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium text-gray-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="auth-forgot-email-input" id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400" style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }} />
                </div>
              </div>

              <Button data-testid="auth-send-reset-btn" type="submit" disabled={loading} className="w-full rounded-full h-14 bg-[#09090b] text-white hover:opacity-90 text-base font-semibold active:scale-95 disabled:opacity-50" style={{ transition: 'opacity 0.2s, transform 0.15s' }}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Code'}
              </Button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="space-y-2">
                <Label htmlFor="reset-code" className="text-sm font-medium text-gray-600">Reset Code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input
                    data-testid="auth-reset-code-input"
                    id="reset-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base text-center tracking-[0.5em] font-mono focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400 placeholder:tracking-normal"
                    style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm font-medium text-gray-600">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="auth-new-password-input" id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} className="pl-11 h-14 bg-gray-50 border-transparent rounded-2xl text-base focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400" style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }} />
                </div>
              </div>

              <Button data-testid="auth-reset-password-btn" type="submit" disabled={loading || resetCode.length !== 6} className="w-full rounded-full h-14 bg-[#09090b] text-white hover:opacity-90 text-base font-semibold active:scale-95 disabled:opacity-50" style={{ transition: 'opacity 0.2s, transform 0.15s' }}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          )}

          {/* Toggle between login/register */}
          {(mode === 'login' || mode === 'register') && (
            <p className="text-center text-sm text-gray-400 mt-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button data-testid="auth-toggle-mode-btn" onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-purple-600 font-medium hover:text-purple-700" style={{ transition: 'color 0.2s' }}>
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          )}

          <p className="text-center text-[11px] text-gray-300 mt-4 leading-relaxed max-w-xs mx-auto animate-fade-in-up" style={{ animationDelay: '0.25s' }} data-testid="auth-privacy-disclaimer">
            By {mode === 'login' ? 'signing in' : 'creating an account'}, you agree to our{' '}
            <a href="/privacy" className="underline hover:text-gray-500">Privacy Policy</a> and{' '}
            <a href="/terms" className="underline hover:text-gray-500">Terms of Service</a>.
            You will receive occasional product updates from Brand OS. Unsubscribe anytime via your email provider. Compliant with CAN-SPAM (USA) and CASL (Canada).
          </p>
        </div>
      </div>
    </div>
  );
}
